import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

// 30 minuti di inattività
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minuti in millisecondi

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inactivityTimer, setInactivityTimer] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
  }, [inactivityTimer]);

  const resetInactivityTimer = useCallback(() => {
    // Cancella il timer esistente
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }

    // Crea nuovo timer solo se l'utente è loggato
    if (token) {
      const timerId = setTimeout(() => {
        console.log('Logout automatico per inattività');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
        window.location.href = '/login-crm'; // Redirect al login
      }, INACTIVITY_TIMEOUT);
      
      setInactivityTimer(timerId);
    }
  }, [token, inactivityTimer]); // Mantieni dependencies

  useEffect(() => {
    // Check if user is logged in on mount
    const savedToken = localStorage.getItem('access_token');
    const savedUser = localStorage.getItem('user');
    
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
    }
    
    setLoading(false);
  }, []);

  // Setup activity listeners
  useEffect(() => {
    if (!token) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => resetInactivityTimer();
    
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Avvia il timer iniziale
    resetInactivityTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
    };
  }, [token]); // Solo token nelle dependencies!

  const login = async (email, password, loginType = 'crm') => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL || '/api';
      
      const response = await fetch(`${backendUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login fallito');
      }

      const data = await response.json();
      
      // Get user info
      const userResponse = await fetch(`${backendUrl}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${data.access_token}`,
        },
      });

      if (!userResponse.ok) {
        throw new Error('Errore nel recupero dati utente');
      }

      const userData = await userResponse.json();
      
      // Check role based on login type
      if (loginType === 'crm' && userData.role === 'cliente') {
        throw new Error('Accesso CRM riservato allo staff');
      }
      
      if (loginType === 'cliente' && userData.role !== 'cliente') {
        throw new Error('Questa è l\'area riservata ai clienti');
      }

      // Save to state and localStorage
      setToken(data.access_token);
      setUser(userData);
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      localStorage.setItem('user', JSON.stringify(userData));

      // Avvia il timer di inattività
      resetInactivityTimer();

      return userData;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!token,
    isAdmin: user?.role === 'admin',
    isSales: user?.role === 'sales',
    isCliente: user?.role === 'cliente',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
